import React, { useState, useEffect, useRef, useCallback } from 'react';

let dotsTimer = null;

const EventTileGrid = () => {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState('');
  const [queryValue, setQueryValue] = useState('');
  const [query, setQuery] = useState('');
  const [startDate, setStartDate] = useState('2025-08-15');
  const [endDate, setEndDate] = useState('2025-08-18');
  const [keywordsValue, setKeywordsValue] = useState('');
  const [keywords, setKeywords] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dots, setDots] = useState(3);
  const [visibleEvents, setVisibleEvents] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const observer = useRef();
  const itemsPerPage = 100;
  
  // Filter events based on search and genre filters - MOVED TO useMemo
  const filteredEvents = React.useMemo(() => {
    return events.filter(event => {
      return true;
      const matchesSearch = event.title.toLowerCase().includes(filter.toLowerCase()) ||
                           (event.spaces[0]?.venueName || '').toLowerCase().includes(filter.toLowerCase());
      return matchesSearch;
    });
  }, [events, filter]);

  const onSearch = (e) => {
    e.preventDefault();
    console.log("Triggering search")
    setQuery(queryValue)
    setKeywords(keywordsValue)
  }

  useEffect(() => {
    if (loading) {
      dotsTimer = setInterval(() => {
        setDots((prev) => (prev+1)%4)
      }, 500)
    } else {
      clearInterval(dotsTimer);
      setDots(3);
    }
  }, [loading])

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError('');
        setEvents([]);
        setVisibleEvents([]);
        const canCache = () => {
          return query.length === 0 && keywords.length === 0
        }
        
        const expiry = localStorage.getItem("eventsExpiry")
        if (canCache() && expiry > new Date().valueOf()) {
            console.log("Fetching from storage")
            const events = localStorage.getItem("events")
            setEvents(JSON.parse(events))
            return
        }

        const server_url = process.env.REACT_APP_SERVER_URL ? process.env.REACT_APP_SERVER_URL : ""
        console.log("Fetching events from API", server_url, query);

        let url = "./events?page_size=3000" +
          "&start_date=" + startDate +
          "&end_date=" + endDate +
          "&keywords=" + encodeURIComponent(keywords) +
          "&recommendation_query=" + encodeURIComponent(query);
        
        const response = await fetch(server_url + url);
        const data = await response.json();
        const now = new Date();
        if (canCache()) {
          localStorage.setItem("eventsExpiry", now.setHours(now.getHours() + 1).valueOf())
          localStorage.setItem("events", JSON.stringify(data))
        }
        setFilter('');
        setEvents(data);
      } catch (error) {
        console.error('Error loading events:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [query, startDate, endDate, keywords]);
  
  // Infinite scroll implementation
  const lastEventElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    }, { threshold: 0, rootMargin: "0px 0px 1000px 0px" });
    
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  // Load more events when page changes or filters change
  useEffect(() => {
    // Skip this effect on initial mount if events aren't loaded yet
    if (events.length === 0) return;
    
    const loadMoreEvents = () => {
      const startIndex = (page - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const newEvents = filteredEvents.slice(startIndex, endIndex);
      
      if (newEvents.length === 0) {
        setHasMore(false);
        return;
      }
      
      if (isInitialLoad || page === 1) {
        setVisibleEvents(newEvents);
        setIsInitialLoad(false);
      } else {
        setVisibleEvents(prev => [...prev, ...newEvents]);
      }
      
      setHasMore(endIndex < filteredEvents.length);
    };
    
    loadMoreEvents();
  }, [page, filteredEvents, isInitialLoad, events.length]);
  
  // Reset page when filters change
  useEffect(() => {
    setPage(1);
    setHasMore(true);
    setIsInitialLoad(true);
  }, [filter]);

  // Event tile component - memo-ized to prevent unnecessary re-renders
  const EventTile = React.memo(({ event }) => (
    <div className="bg-white rounded overflow-hidden flex flex-col h-full border border-gray-200 text-xs sm:text-sm">
      <div className="h-24 sm:h-32 bg-gray-200 relative">
        {event.recommendation ? (
          <div>
            <div className="absolute inset-1 flex justify-top items-top gap-1">
              <span className="px-1 rounded text-lg">🤖</span>
            </div>
            <div className="absolute inset-0 overflow-y-scroll text-xs flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
              <div className="bg-black text-white px-2 py-1 rounded">{event.explanation}</div>
            </div>
          </div>
        ) : <div />}
        {event.images && event.images.length > 0 ? (
          <img 
            src={event.images.filter(i => i.imageType === "Small")[0].url.replace("http://", "https://")}
            alt={event.title}
            width="180"
            height="120"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-300">
            <span className="text-gray-500">No image</span>
          </div>
        )}
      </div>
      
      <div className="p-2 flex-grow flex flex-col">
        <h3 className="font-bold text-sm sm:text-base line-clamp-2 leading-tight"><a href={"https://www.edfringe.com/tickets/whats-on/" + event.slug}>{event.title}</a></h3>
        
        <div className="text-xs text-gray-600 mt-1">
          <div className="truncate">{event.spaces[0]?.venueName || 'No venue'}</div>
          <div className="flex justify-between">
            <span>{event.datesDisplay}</span>
            <span>{event.duration}m</span>
          </div>
        </div>
        
        <div className="mt-1 flex items-center text-xs">
          <div className="ml-auto text-gray-500">{event.id}</div>
        </div>
      </div>
    </div>
  ));

  return (
    <div className="mx-auto px-2 py-3 h-screen overflow-y-auto" style={{ scrollBehavior: 'smooth' }}>
      <h1 className="text-2xl mb-3"><i className="font-bold">Fr</i><i className="">inge</i><i className="font-bold">App</i></h1>

      {/* Search parameters */}
      <form action="" onSubmit={(e) => onSearch(e)} className="mb-3 flex items-center gap-2 sticky top-0 bg-white py-2 z-10 border-b">
        <div className="flex-grow">
          <input
            type="text"
            placeholder="Ask AI e.g. show me shows about dogs..."
            className="w-full p-1 border border-gray-300 rounded text-sm"
            value={queryValue}
            onChange={(e) => setQueryValue(e.target.value)}
          />
          <input
            type="text"
            placeholder="Keyword search"
            className="p-1 m-1 border border-gray-300 rounded text-sm"
            value={keywordsValue}
            onChange={(e) => setKeywordsValue(e.target.value)}
          />
          from <input
            type="date"
            className="p-1 m-1 border border-gray-300 rounded text-sm"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          to <input
            type="date"
            className="p-1 m-1 border border-gray-300 rounded text-sm"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <button type="submit">Search</button>
        <div className="text-xs text-gray-600 whitespace-nowrap">
          {filteredEvents.length} events
        </div>
      </form>

      {/* Loading state - initial load */}
      {loading && events.length === 0 && (
        <div className="flex justify-center items-center h-64">
          <div className="text-xl">{query !== "" ? "Frapp AI is thinking" : "Loading events"}{".".repeat(dots)}</div>
        </div>
      )}

      {/* No results */}
      {!loading && filteredEvents.length === 0 && events.length > 0 && (
        <div className="flex justify-center items-center h-64">
          <div className="text-base">No events match your filters</div>
        </div>
      )}

        {/* No results */}
        {!loading && error != "" && (
        <div className="flex justify-center items-center h-64">
          <div className="text-base">Server Error: {error}</div>
        </div>
      )}
      
      {/* Grid of events with infinite scrolling */}
      <div className="my-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-8 gap-2 sm:gap-3">
        {visibleEvents.map((event, index) => {
          if (visibleEvents.length === index + 1) {
            return (
              <div ref={lastEventElementRef} key={event.id}>
                <EventTile event={event} />
              </div>
            );
          } else {
            return <EventTile key={event.id} event={event} />;
          }
        })}
      </div>
      
      {/* Loading indicator */}
      {loading && events.length > 0 && (
        <div className="flex justify-center items-center py-4">
          <div className="text-sm">Loading more events...</div>
        </div>
      )}
      
      {/* End of results message */}
      {!hasMore && visibleEvents.length > 0 && (
        <div className="text-center text-sm text-gray-500 py-4">
          No more events to load
        </div>
      )}
      
      {/* Data fetching status indicator */}
      <div className="fixed bottom-4 right-4 z-20">
        {loading && (
          <div className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs shadow-lg">
            Loading...
          </div>
        )}
      </div>
    </div>
  );
};

export default EventTileGrid;